import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import HeartFilled from './heart-filled.svg';
import HeartOutline from './heart-outline.svg';
import { DXAccountContext } from '@contexts/DXAccountContext';
import TopFeaturesStyles from './TopFeatures.styled';
import ChevronDown from './chevron-down.svg';
import ChevronUp from './chevron-up.svg';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';

const TopFeatures = ({ features }) => {
  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth < variables.desktop_breakpoint;
  const [likedFeatures, setLikedFeatures] = useState([]);
  const [likedFeaturesNotInPortal, setLikedFeaturesNotInPortal] = useState([]);
  const [isChevronOpen, setIsChevronOpen] = useState(!isMobile);
  const accountContext = useContext(DXAccountContext);

  const handleFavoriteFeatureItems = (items) => {
    items.forEach((f) => {
      if (
        likedFeatures?.find((likedFeature) => likedFeature.name === f.name) ||
        likedFeaturesNotInPortal.includes(f.name)
      ) {
        f.favorite = true;
      } else {
        f.favorite = false;
      }
    });

    return items.sort((b, a) => a.favorite - b.favorite);
  };

  const [displayFeatureItems, setDisplayFeatureItems] = useState(
    isMobile
      ? handleFavoriteFeatureItems(
          features.map((f) => ({ favorite: false, name: f }))
        )
      : handleFavoriteFeatureItems(
          features.map((f) => ({ favorite: false, name: f }))
        ).slice(0, 3)
  );

  const handleFeatureClick = (featureName) => {
    const _feature = accountContext.state.features?.find(
      (feature) => feature.name === featureName
    );

    if (_feature) {
      const isLiked = !!likedFeatures
        .map((feature) => feature.name)
        .includes(featureName);
      const profileId = accountContext.state.account?.profile?.profileId || 0;

      if (!isLiked) {
        accountContext.actions.addFeature({
          featureId: _feature.featureId,
          profileId,
        });
        likedFeatures && setLikedFeatures([...likedFeatures, _feature]);
      } else if (isLiked) {
        accountContext.actions.removeFeature({
          featureId: _feature.featureId,
          profileId,
        });
        likedFeatures &&
          setLikedFeatures(
            likedFeatures.filter((feature) => feature.name !== featureName)
          );
      }
    } else {
      // some homes have features not in the home aspirations in the portal
      // these features can be added or removed here and don't count towards the limit
      const isLikedNotInPortal =
        !!likedFeaturesNotInPortal.includes(featureName);

      if (!isLikedNotInPortal) {
        likedFeaturesNotInPortal &&
          setLikedFeaturesNotInPortal([
            ...likedFeaturesNotInPortal,
            featureName,
          ]);
      } else if (isLikedNotInPortal) {
        likedFeaturesNotInPortal &&
          setLikedFeaturesNotInPortal(
            likedFeaturesNotInPortal.filter((name) => name !== featureName)
          );
      }
    }
  };

  useEffect(() => {
    if (accountContext.state.account?.profile?.likedFeatures) {
      setLikedFeatures(accountContext.state.account.profile.likedFeatures);
      setDisplayFeatureItems(handleFavoriteFeatureItems(displayFeatureItems));
    }
  }, [accountContext.state.account?.profile?.likedFeatures]);

  useEffect(() => {
    const newFeaturesList = handleFavoriteFeatureItems(
      features.map((f) => ({ favorite: false, name: f }))
    );
    if (isChevronOpen) {
      setDisplayFeatureItems(newFeaturesList);
    } else {
      setDisplayFeatureItems(
        newFeaturesList.length > 3
          ? newFeaturesList.slice(0, 3)
          : newFeaturesList
      );
    }
  }, [likedFeatures]);

  if (!features || features?.length === 0) {
    return <></>;
  }

  const handleChevronClick = (isOpen) => {
    setIsChevronOpen(isOpen);

    const newFeatures = handleFavoriteFeatureItems(
      features.map((f) => ({ favorite: false, name: f }))
    );

    if (isOpen) {
      setDisplayFeatureItems(newFeatures);
    } else {
      setDisplayFeatureItems(
        newFeatures.length > 3 ? newFeatures.slice(0, 3) : newFeatures
      );
    }
  };

  return (
    <TopFeaturesStyles>
      <div className="top-feature-title-section">
        <div className="top-features-title-heart">
          <HeartFilled viewBox="0 0 16 16" />
        </div>
        <div>
          <h3 className="top-features-title">Top Features</h3>
          <p className="top-features-subheading">
            Save your Favorite Features to your MyHome Account
          </p>
        </div>
      </div>

      <ul className="features-list">
        {displayFeatureItems?.map((feature, i) => {
          return (
            <li
              key={`feature-${i + 1}`}
              className="features-list-item"
              onClick={() =>
                accountContext.state.account
                  ? handleFeatureClick(feature.name)
                  : accountContext.actions.accountLogin()
              }
            >
              {feature.favorite ? (
                <HeartFilled viewBox="0 0 16 16" />
              ) : (
                <HeartOutline viewBox="0 0 16 16" />
              )}{' '}
              {feature.name}
            </li>
          );
        })}
        <li
          className="top-features-chevron"
          onClick={() => handleChevronClick(!isChevronOpen)}
        >
          {!isChevronOpen ? (
            <ChevronDown viewBox="0 0 16 16"></ChevronDown>
          ) : (
            <ChevronUp viewBox="0 0 16 16"></ChevronUp>
          )}
        </li>
      </ul>

      {likedFeatures?.length >= 5 && (
        <p className="error-state">
          Looks like you’ve picked your top five!{' '}
          <a href="/portal/planner/aspirations/">Visit your account</a> to see
          your favorites.
        </p>
      )}
    </TopFeaturesStyles>
  );
};

export default TopFeatures;

TopFeatures.propTypes = {
  features: PropTypes.array,
};
